<template>
  <div
    class="subscription-form"
    :class="{ 'subscription-form--dark': !themeOverride && isDarkTheme }"
  >
    <div class="subscribe">
      <p class="subscribe__title">{{ $t('SUBSCRIBE_TITLE') }}</p>
      <i18n tag="p" class="subscribe__desc" path="SUBSCRIBE_DETAIL">
        <a :href="$t('SUBSCRIBE_LINK')">{{ $t('SUBSCRIBE_LINK_TEXT') }}</a>
      </i18n>

      <sh-form @submit="handleSubscribe">
        <text-input
          ref="textInput"
          id="subscribe_email"
          class="subscribe-form__input"
          v-model="email"
          v-validate="'required|email'"
          data-vv-validate-on="blur"
          :error-message="errors.first($t('SUBSCRIBE_LABEL'))"
          :success-message="$t(successText)"
          :name="$t('SUBSCRIBE_LABEL')"
          :label="$t('SUBSCRIBE_LABEL')"
          :theme-override="themeOverride"
        />

        <template #submit>
          <sh-button
            type="submit"
            class="subscribe-form__btn"
            :theme-override="themeOverride"
          >
            {{ $t('SUBSCRIBE_SUBMIT') }}
          </sh-button>
        </template>
      </sh-form>
    </div>

    <recaptcha-terms :theme-override="themeOverride" />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import { ShForm, TextInput, ShButton } from '@sohohouse/design-system'

import RecaptchaTerms from '~/components/recaptcha-terms'
import RecaptchaV3 from '~/mixins/recaptcha'
import location from '~/utils/location'
import { FORM, EVENT } from '~/constants/analytics'
import ThemeMixin from '~/mixins/theme'

import SUBSCRIBE_ERRORS from './error-messages'

const { mapActions: mapAccountActions, mapGetters: mapAccountGetters } = createNamespacedHelpers('user/account')

export default {
  name: 'SubscribeForm',

  $_veeValidate: {
    validator: 'new'
  },

  components: {
    ShForm,
    TextInput,
    ShButton,
    RecaptchaTerms
  },

  mixins: [ThemeMixin, RecaptchaV3],

  props: {
    themeOverride: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      email: '',
      success: false,
      policy: true, // TODO - designs currently don't include a policy cehckbox,
      timer: null
    }
  },

  mounted () {
    this.$validator.reset()
    this.$validator.localize(this.$i18n.locale, {
      messages: {
        required: this.$t('SUBSCRIBE_REQUIRED_EMAIL'),
        email: this.$t('SUBSCRIBE_INVALID_EMAIL')
      }
    })
  },

  destroyed () {
    clearTimeout(this.timer)
  },

  computed: {
    ...mapAccountGetters(['failed', 'errorMessages']),

    isInvalid () {
      return Object.values(this.veeValidateFields)
        .some(field => (field.required && field.pristine) || field.invalid)
    },

    successText () {
      return this.success ? 'SUBSCRIBE_SUCCESS' : ''
    }
  },

  watch: {
    email (value) {
      if (value.length > 0 && this.success) {
        this.success = false
      }
    }
  },

  methods: {
    ...mapAccountActions(['subscribe']),

    async handleSubscribe () {
      await this.$validator.validate()

      if (this.isInvalid) {
        return
      }

      const token = await this.getRecaptchaToken()

      const params = {
        email: this.email,
        pageId: location.pathname,
        privacyPolicyConsent: this.policy
      }

      await this.subscribe({ params, token })

      if (this.failed) {
        this.$analytics.error({
          event: EVENT.FORM_ERROR,
          form: {
            id: FORM.NEWSLETTER,
            error: this.errorMessages
          }
        })
        this.handleErrors()
      } else {
        this.resetForm()
        this.success = true

        this.$analytics.push({
          event: EVENT.FORM_SUCCESS,
          form: {
            id: FORM.NEWSLETTER
          }
        })

        // remove focus from input when enter is pressed
        this.removeFocus()
        this.timer = setTimeout(() => {
          this.success = false
        }, 5000)
      }
    },

    handleErrors () {
      // will be undefined if missing network connection
      if (!this.errorMessages || !this.errorMessages.length) {
        this.errors.add({
          field: this.$t('SUBSCRIBE_LABEL'),
          msg: this.$t('SUBSCRIBE_UNEXPECTED_ERROR')
        })
      } else {
        this.errorMessages.forEach(({ code }) => this.errors.add({
          field: this.$t('SUBSCRIBE_LABEL'),
          msg: this.$t(SUBSCRIBE_ERRORS[code])
        }))
      }
    },

    removeFocus () {
      this.$refs.textInput.$refs.input.blur()
    },

    resetForm () {
      this.email = ''
      this.$validator.reset()
    }
  }
}
</script>

<style lang="scss" scoped>
.subscription-form {
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  form {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;

    @include tablet {
      flex-wrap: nowrap;
    }
  }

  &__input {
    width: 100%;
    @include stack-space($space-050);

    @include tablet {
      @include stack-space($space-060);
    }
  }

  &__btn {
    width: fit-content;
  }

  &--dark {
    a {
      color: $color-white;
    }
  }
}

.subscribe {
  @include stack-space($space-090);

  @include tablet {
    @include reset;
  }

  &__title {
    @include font('070');
    @include stack-space($space-020);
  }

  &__desc {
    @include font('050');
    @include stack-space($space-050);
  }
}

</style>
